import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  Plus, X, Trash2, Send, Pencil, Clock, CheckCircle2, Globe, BookOpen, Mail,
  Megaphone, ClipboardList, Eye, Calendar, BarChart3, Copy, Monitor, Smartphone, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const MARKETING_EMAIL = "marketing@combinedwork.com";

const TABS = [
  { key: "blog", label: "Blog & Website", icon: BookOpen },
  { key: "email", label: "Email Campaigns", icon: Mail },
  { key: "social", label: "Social Media", icon: Megaphone },
  { key: "requests", label: "Marketing Requests", icon: ClipboardList },
  { key: "analytics", label: "Website Analytics", icon: BarChart3 },
];

const TOOLTIP_STYLE = {
  contentStyle: { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, color: "#111827", fontSize: 12 },
  labelStyle: { color: "#6B7280" },
};
const DAY_RANGES = [7, 30, 90];
const CHART_PRIMARY = "#7c3aed"; // violet-600
const CHART_SECONDARY = "#d97706"; // amber-600

const BLOG_BLANK = { title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", status: "draft" };
const CAMPAIGN_BLANK = { subject: "", preview_text: "", body_html: "", audience: "all_contacts", recipient_emails: "", scheduled_at: "" };
const SOCIAL_BLANK = { platform: "linkedin", content: "", media_url: "", link_url: "", scheduled_at: "", status: "draft" };
const REQUEST_BLANK = { title: "", description: "", request_type: "blog", priority: "medium", due_date: "" };

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", color: "bg-blue-50 text-blue-700 border border-blue-200" },
  { key: "twitter", label: "X / Twitter", color: "bg-gray-50 text-gray-800 border border-gray-300" },
  { key: "facebook", label: "Facebook", color: "bg-indigo-50 text-indigo-700 border border-indigo-200" },
  { key: "instagram", label: "Instagram", color: "bg-pink-50 text-pink-700 border border-pink-200" },
  { key: "tiktok", label: "TikTok", color: "bg-slate-100 text-slate-700 border border-slate-200" },
  { key: "youtube", label: "YouTube", color: "bg-red-50 text-red-700 border border-red-200" },
  { key: "other", label: "Other", color: "bg-gray-100 text-gray-600 border border-gray-200" },
];
const platformMeta = (k) => PLATFORMS.find((p) => p.key === k) || PLATFORMS[PLATFORMS.length - 1];

const REQUEST_TYPES = [
  { key: "blog", label: "Blog post" },
  { key: "social", label: "Social media" },
  { key: "email", label: "Email campaign" },
  { key: "design", label: "Design asset" },
  { key: "campaign", label: "Full campaign" },
  { key: "other", label: "Other" },
];

const PRIORITIES = [
  { key: "low", label: "Low", color: "bg-gray-100 text-gray-600" },
  { key: "medium", label: "Medium", color: "bg-blue-100 text-blue-700" },
  { key: "high", label: "High", color: "bg-amber-100 text-amber-800" },
  { key: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];
const priorityMeta = (k) => PRIORITIES.find((p) => p.key === k) || PRIORITIES[1];

const REQUEST_STATUSES = ["new", "in_progress", "in_review", "done"];

const inputClass = "w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

const StatTile = ({ label, value }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</div>
    <div className="text-xl font-bold text-gray-900 mt-1">{value ?? "—"}</div>
  </div>
);

const EmptyState = ({ icon: Icon, text }) => (
  <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
    <div className="w-14 h-14 rounded-2xl bg-violet-100 grid place-items-center mx-auto mb-4">
      <Icon className="w-7 h-7 text-violet-500" />
    </div>
    <p className="text-sm text-gray-400 max-w-sm mx-auto">{text}</p>
  </div>
);

const RankedList = ({ title, icon: Icon, rows, emptyText }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
      <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-4 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-400 italic">{emptyText}</div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex items-center justify-between gap-3 text-xs mb-1">
                <span className="truncate text-gray-700" title={r.label}>{r.label}</span>
                <span className="text-gray-400 font-mono shrink-0">{r.value}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-600 rounded-full" style={{ width: `${Math.max(4, (r.value / max) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DeviceSplit = ({ desktop = 0, mobile = 0 }) => {
  const total = desktop + mobile;
  const desktopPct = total ? Math.round((desktop / total) * 100) : 0;
  const mobilePct = total ? 100 - desktopPct : 0;
  return (
    <div>
      <div className="flex h-3 w-full bg-gray-100 rounded-full gap-0.5 overflow-hidden" data-testid="device-split-bar">
        <div className="h-full bg-violet-600" style={{ width: `${desktopPct}%` }} />
        <div className="h-full bg-amber-600" style={{ width: `${mobilePct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-4 text-xs flex-wrap gap-2">
        <div className="flex items-center gap-2 text-gray-700">
          <Monitor className="w-3.5 h-3.5 text-violet-600" /> Desktop
          <span className="text-gray-400 font-mono">{desktopPct}% · {desktop}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <Smartphone className="w-3.5 h-3.5 text-amber-600" /> Mobile
          <span className="text-gray-400 font-mono">{mobilePct}% · {mobile}</span>
        </div>
      </div>
    </div>
  );
};

const TrackingSnippet = ({ tenantId }) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://combinedwork.com";
  const snippet = `<script>
(function(){
  var vid = localStorage.getItem('cw_vid');
  if(!vid){ vid = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('cw_vid', vid); }
  var img = new Image();
  img.referrerPolicy = 'no-referrer-when-downgrade';
  img.src = '${origin}/api/public/analytics/track.gif?site=${tenantId || "YOUR_TENANT_ID"}'
    + '&path=' + encodeURIComponent(location.pathname)
    + '&title=' + encodeURIComponent(document.title)
    + '&ref=' + encodeURIComponent(document.referrer)
    + '&vid=' + vid;
})();
</script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Snippet copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Tracking snippet</div>
        <button onClick={copy} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-violet-700" data-testid="copy-analytics-snippet">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>
      <pre className="text-[11px] font-mono text-gray-700 whitespace-pre-wrap break-all bg-gray-50 border border-gray-200 rounded-xl p-3 max-h-48 overflow-y-auto">{snippet}</pre>
      <p className="text-[11px] text-gray-400 mt-3">
        Paste this before <code className="text-gray-600">&lt;/head&gt;</code> on any page you want tracked — blog, landing pages, docs.
      </p>
    </div>
  );
};

export default function Marketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState("blog");
  const [summary, setSummary] = useState(null);

  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [posts, setPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socialPosts, setSocialPosts] = useState([]);
  const [requests, setRequests] = useState([]);

  const [blogOpen, setBlogOpen] = useState(false);
  const [blogForm, setBlogForm] = useState(BLOG_BLANK);
  const [editingPostId, setEditingPostId] = useState(null);

  const [campaignOpen, setCampaignOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState(CAMPAIGN_BLANK);

  const [socialOpen, setSocialOpen] = useState(false);
  const [socialForm, setSocialForm] = useState(SOCIAL_BLANK);
  const [editingSocialId, setEditingSocialId] = useState(null);

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestForm, setRequestForm] = useState(REQUEST_BLANK);

  const load = useCallback(async () => {
    try {
      const [s, b, c, sp, r] = await Promise.all([
        api.get("/marketing/summary").catch(() => ({ data: null })),
        api.get("/marketing/blog-posts"),
        api.get("/marketing/email-campaigns"),
        api.get("/marketing/social-posts"),
        api.get("/marketing/requests"),
      ]);
      setSummary(s.data);
      setPosts(b.data || []);
      setCampaigns(c.data || []);
      setSocialPosts(sp.data || []);
      setRequests(r.data || []);
    } catch {
      toast.error("Failed to load marketing data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const analyticsReqRef = useRef(0);

  const loadAnalytics = useCallback(async (days) => {
    // Bumping this invalidates any still-in-flight load, so switching the
    // day range quickly can't have a slower earlier range's response
    // overwrite the range currently selected.
    const reqId = ++analyticsReqRef.current;
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get(`/marketing/analytics/summary?days=${days}`);
      if (reqId !== analyticsReqRef.current) return;
      setAnalytics(data);
    } catch {
      if (reqId === analyticsReqRef.current) toast.error("Failed to load analytics");
    } finally {
      if (reqId === analyticsReqRef.current) setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "analytics") loadAnalytics(analyticsDays); }, [tab, analyticsDays, loadAnalytics]);

  if (user?.role !== "superadmin" && user?.role !== "marketing_admin") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4">
        <ShieldAlert className="w-10 h-10 text-red-500" />
        <div className="text-lg font-bold text-gray-900">Access denied</div>
        <div className="text-sm text-gray-500 text-center max-w-sm">This page is restricted to superadmin and marketing accounts.</div>
      </div>
    );
  }

  // ── Blog posts ──
  const openNewPost = () => { setEditingPostId(null); setBlogForm(BLOG_BLANK); setBlogOpen(true); };
  const openEditPost = (p) => {
    setEditingPostId(p.post_id);
    setBlogForm({
      title: p.title, slug: p.slug, excerpt: p.excerpt || "", content: p.content || "",
      cover_image: p.cover_image || "", tags: (p.tags || []).join(", "), status: p.status,
    });
    setBlogOpen(true);
  };
  const saveBlogPost = async () => {
    if (!blogForm.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      title: blogForm.title, slug: blogForm.slug || undefined, excerpt: blogForm.excerpt,
      content: blogForm.content, cover_image: blogForm.cover_image, status: blogForm.status,
      tags: blogForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editingPostId) {
        await api.put(`/marketing/blog-posts/${editingPostId}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/marketing/blog-posts", payload);
        toast.success("Post created");
      }
      setBlogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save post"); }
  };
  const togglePublish = async (p) => {
    try {
      await api.put(`/marketing/blog-posts/${p.post_id}`, { status: p.status === "published" ? "draft" : "published" });
      toast.success(p.status === "published" ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed to update"); }
  };
  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/marketing/blog-posts/${p.post_id}`); toast.success("Post deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  // ── Email campaigns ──
  const openNewCampaign = () => { setCampaignForm(CAMPAIGN_BLANK); setCampaignOpen(true); };
  const saveCampaign = async () => {
    if (!campaignForm.subject.trim() || !campaignForm.body_html.trim()) { toast.error("Subject and body are required"); return; }
    const payload = {
      subject: campaignForm.subject,
      preview_text: campaignForm.preview_text,
      body_html: campaignForm.body_html,
      audience: campaignForm.audience,
      recipient_emails: campaignForm.audience === "custom"
        ? campaignForm.recipient_emails.split(/[\n,]/).map((e) => e.trim()).filter(Boolean)
        : [],
      scheduled_at: campaignForm.scheduled_at ? new Date(campaignForm.scheduled_at).toISOString() : null,
    };
    try {
      await api.post("/marketing/email-campaigns", payload);
      toast.success("Campaign saved as draft");
      setCampaignOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save campaign"); }
  };
  const sendCampaign = async (c) => {
    if (!window.confirm(`Send "${c.subject}" now from ${MARKETING_EMAIL}?`)) return;
    try {
      const { data } = await api.post(`/marketing/email-campaigns/${c.campaign_id}/send`);
      toast.success(`Sent to ${data.sent_count} recipient${data.sent_count === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send campaign"); }
  };
  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete draft "${c.subject}"?`)) return;
    try { await api.delete(`/marketing/email-campaigns/${c.campaign_id}`); toast.success("Campaign deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  // ── Social posts ──
  const openNewSocial = () => { setEditingSocialId(null); setSocialForm(SOCIAL_BLANK); setSocialOpen(true); };
  const openEditSocial = (p) => {
    setEditingSocialId(p.post_id);
    setSocialForm({
      platform: p.platform, content: p.content, media_url: p.media_url || "", link_url: p.link_url || "",
      scheduled_at: p.scheduled_at ? p.scheduled_at.slice(0, 16) : "", status: p.status,
    });
    setSocialOpen(true);
  };
  const saveSocialPost = async () => {
    if (!socialForm.content.trim()) { toast.error("Content is required"); return; }
    const payload = {
      platform: socialForm.platform, content: socialForm.content, media_url: socialForm.media_url,
      link_url: socialForm.link_url, status: socialForm.status,
      scheduled_at: socialForm.scheduled_at ? new Date(socialForm.scheduled_at).toISOString() : null,
    };
    try {
      if (editingSocialId) {
        await api.put(`/marketing/social-posts/${editingSocialId}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/marketing/social-posts", payload);
        toast.success("Post added to calendar");
      }
      setSocialOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save post"); }
  };
  const markPosted = async (p) => {
    try { await api.put(`/marketing/social-posts/${p.post_id}`, { status: "posted" }); toast.success("Marked as posted"); load(); }
    catch { toast.error("Failed to update"); }
  };
  const deleteSocial = async (p) => {
    if (!window.confirm("Delete this social post?")) return;
    try { await api.delete(`/marketing/social-posts/${p.post_id}`); toast.success("Post deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  // ── Marketing requests ──
  const openNewRequest = () => { setRequestForm(REQUEST_BLANK); setRequestOpen(true); };
  const saveRequest = async () => {
    if (!requestForm.title.trim()) { toast.error("Title is required"); return; }
    try {
      await api.post("/marketing/requests", { ...requestForm, due_date: requestForm.due_date || null });
      toast.success("Request submitted to the marketing team");
      setRequestOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit request"); }
  };
  const updateRequestStatus = async (r, status) => {
    try { await api.put(`/marketing/requests/${r.request_id}`, { status }); load(); }
    catch { toast.error("Failed to update — you may need writer access"); }
  };
  const deleteRequest = async (r) => {
    if (!window.confirm(`Delete request "${r.title}"?`)) return;
    try { await api.delete(`/marketing/requests/${r.request_id}`); toast.success("Request deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const newButton = {
    blog: { label: "New post", onClick: openNewPost },
    email: { label: "New campaign", onClick: openNewCampaign },
    social: { label: "New social post", onClick: openNewSocial },
    requests: { label: "New request", onClick: openNewRequest },
  }[tab];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-600 mb-0.5">Marketing Hub</p>
          <h1 className="text-2xl font-bold text-gray-900">Marketing</h1>
          <p className="text-sm text-gray-500 mt-1">
            Blog & website content, email campaigns, social media calendar, requests, and site analytics — sent from {MARKETING_EMAIL}.
          </p>
        </div>
        {newButton && (
          <Button
            onClick={newButton.onClick}
            className="rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md shadow-violet-200"
            data-testid="marketing-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> {newButton.label}
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6 mb-2">
          <StatTile label="Published posts" value={summary.published_posts} />
          <StatTile label="Draft posts" value={summary.draft_posts} />
          <StatTile label="Campaigns sent" value={summary.sent_campaigns} />
          <StatTile label="Scheduled social" value={summary.scheduled_social} />
          <StatTile label="Open requests" value={summary.open_requests} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6 mt-4 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === key ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
            data-testid={`marketing-tab-${key}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ── Blog & Website tab ── */}
      {tab === "blog" && (
        posts.length === 0 ? (
          <EmptyState icon={BookOpen} text="No blog posts yet. Write your first one." />
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.post_id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md hover:border-violet-100 transition-all" data-testid={`blog-row-${p.post_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{p.title}</span>
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                    </div>
                    {p.excerpt && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.excerpt}</p>}
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-gray-400">
                      {p.status === "published" ? (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">/blog/{p.slug} ↗</a>
                      ) : (
                        <span>/blog/{p.slug}</span>
                      )}
                      <span>{p.author_name}</span>
                      {p.tags?.length > 0 && <span>{p.tags.join(", ")}</span>}
                      <span>{p.published_at ? `Published ${new Date(p.published_at).toLocaleDateString()}` : `Updated ${new Date(p.updated_at).toLocaleDateString()}`}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => togglePublish(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50" title={p.status === "published" ? "Unpublish" : "Publish"} data-testid={`publish-post-${p.post_id}`}>
                      {p.status === "published" ? <Eye className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEditPost(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50" data-testid={`edit-post-${p.post_id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deletePost(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-post-${p.post_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Email campaigns tab ── */}
      {tab === "email" && (
        campaigns.length === 0 ? (
          <EmptyState icon={Mail} text="No campaigns yet. Draft your first email." />
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c.campaign_id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md hover:border-violet-100 transition-all" data-testid={`campaign-row-${c.campaign_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{c.subject}</span>
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${c.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{c.status}</span>
                    </div>
                    {c.preview_text && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{c.preview_text}</p>}
                    <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-gray-400">
                      <span>{c.audience === "all_contacts" ? "All contacts" : `${c.recipient_emails?.length || 0} custom recipients`}</span>
                      {c.status === "sent" && <span>Sent to {c.sent_count} · {c.failed_count > 0 ? `${c.failed_count} failed` : "0 failed"}</span>}
                      <span>From {MARKETING_EMAIL}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {c.status !== "sent" && (
                      <button onClick={() => sendCampaign(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50" title="Send now" data-testid={`send-campaign-${c.campaign_id}`}>
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {c.status !== "sent" && (
                      <button onClick={() => deleteCampaign(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-campaign-${c.campaign_id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── Social media tab ── */}
      {tab === "social" && (
        socialPosts.length === 0 ? (
          <EmptyState icon={Megaphone} text="No social posts scheduled yet." />
        ) : (
          <div className="space-y-3">
            {socialPosts.map((p) => {
              const meta = platformMeta(p.platform);
              return (
                <div key={p.post_id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md hover:border-violet-100 transition-all" data-testid={`social-row-${p.post_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${meta.color}`}>{meta.label}</span>
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
                          p.status === "posted" ? "bg-emerald-100 text-emerald-700" : p.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                        }`}>{p.status}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1.5 line-clamp-2">{p.content}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-gray-400">
                        {p.scheduled_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.scheduled_at).toLocaleString()}</span>}
                        {p.link_url && <span className="truncate max-w-xs">{p.link_url}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {p.status !== "posted" && (
                        <button onClick={() => markPosted(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50" title="Mark as posted" data-testid={`mark-posted-${p.post_id}`}>
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => openEditSocial(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50" data-testid={`edit-social-${p.post_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteSocial(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-social-${p.post_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Marketing requests tab ── */}
      {tab === "requests" && (
        requests.length === 0 ? (
          <EmptyState icon={ClipboardList} text="No requests yet. Other teams can ask marketing for help here." />
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const pr = priorityMeta(r.priority);
              return (
                <div key={r.request_id} className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md hover:border-violet-100 transition-all" data-testid={`request-row-${r.request_id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{r.title}</span>
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${pr.color}`}>{pr.label}</span>
                        <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-gray-100 text-gray-600">
                          {REQUEST_TYPES.find((t) => t.key === r.request_type)?.label || r.request_type}
                        </span>
                      </div>
                      {r.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{r.description}</p>}
                      <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-gray-400">
                        <span>Requested by {r.requested_by_name}</span>
                        {r.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Due {new Date(r.due_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={r.status}
                        onChange={(e) => updateRequestStatus(r, e.target.value)}
                        className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-300"
                        data-testid={`request-status-${r.request_id}`}
                      >
                        {REQUEST_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace("_", " ")}</option>)}
                      </select>
                      <button onClick={() => deleteRequest(r)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-request-${r.request_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── Website analytics tab ── */}
      {tab === "analytics" && (
        <div className="space-y-6">
          <div className="flex bg-gray-50 border border-gray-200 rounded-xl overflow-hidden w-fit">
            {DAY_RANGES.map((d) => (
              <button
                key={d}
                onClick={() => setAnalyticsDays(d)}
                className={`px-4 py-2 text-sm ${
                  analyticsDays === d ? "bg-white text-violet-700 font-medium shadow-sm" : "text-gray-500 hover:text-gray-800"
                }`}
                data-testid={`analytics-range-${d}`}
              >
                Last {d}d
              </button>
            ))}
          </div>

          {!analytics && analyticsLoading && (
            <div className="p-10 text-center text-sm text-gray-400">Loading analytics…</div>
          )}

          {analytics && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatTile label="Pageviews" value={analytics.total_pageviews} />
                <StatTile label="Unique visitors" value={analytics.unique_visitors} />
                <StatTile label="Avg. views / day" value={Math.round(analytics.total_pageviews / analytics.days) || 0} />
              </div>

              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-4">Traffic trend</div>
                {analytics.total_pageviews === 0 ? (
                  <EmptyState icon={BarChart3} text="No traffic recorded yet. Install the tracking snippet below to start collecting data." />
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={analytics.daily_series}>
                      <CartesianGrid stroke="#F3F4F6" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#9CA3AF"
                        fontSize={11}
                        tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      />
                      <YAxis stroke="#9CA3AF" fontSize={11} allowDecimals={false} />
                      <Tooltip
                        {...TOOLTIP_STYLE}
                        labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, color: "#6B7280" }} />
                      <Area type="monotone" dataKey="views" name="Pageviews" stroke={CHART_PRIMARY} fill={CHART_PRIMARY} fillOpacity={0.12} strokeWidth={2} />
                      <Area type="monotone" dataKey="visitors" name="Unique visitors" stroke={CHART_SECONDARY} fill={CHART_SECONDARY} fillOpacity={0.12} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RankedList
                  title="Top pages"
                  icon={Eye}
                  rows={analytics.top_pages.map((p) => ({ label: p.path, value: p.views }))}
                  emptyText="No pageviews yet."
                />
                <RankedList
                  title="Top referrers"
                  icon={Globe}
                  rows={analytics.top_referrers.map((r) => ({ label: r.domain, value: r.views }))}
                  emptyText="No referrer data yet."
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-4">Device split</div>
                  <DeviceSplit desktop={analytics.device_breakdown.desktop} mobile={analytics.device_breakdown.mobile} />
                </div>
                <TrackingSnippet tenantId={user?.tenant_id} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Blog post modal ── */}
      {blogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">{editingPostId ? "Edit post" : "New blog / website post"}</h2>
              <button onClick={() => setBlogOpen(false)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Title</label>
                <input className={inputClass} value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} data-testid="blog-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Slug</label>
                  <input className={inputClass} placeholder="auto-generated from title" value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} data-testid="blog-slug-input" />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select className={inputClass} value={blogForm.status} onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })} data-testid="blog-status-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Cover image URL</label>
                <input className={inputClass} value={blogForm.cover_image} onChange={(e) => setBlogForm({ ...blogForm, cover_image: e.target.value })} data-testid="blog-cover-input" />
              </div>
              <div>
                <label className={labelClass}>Excerpt</label>
                <textarea className={`${inputClass} resize-none`} rows={2} value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} data-testid="blog-excerpt-input" />
              </div>
              <div>
                <label className={labelClass}>Content</label>
                <textarea className={`${inputClass} font-mono text-xs resize-none`} rows={10} value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} placeholder="Markdown or HTML" data-testid="blog-content-input" />
              </div>
              <div>
                <label className={labelClass}>Tags (comma separated)</label>
                <input className={inputClass} value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} data-testid="blog-tags-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveBlogPost} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="confirm-save-post">
                {editingPostId ? "Save changes" : "Create post"}
              </Button>
              <Button onClick={() => setBlogOpen(false)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Email campaign modal ── */}
      {campaignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">New email campaign</h2>
              <button onClick={() => setCampaignOpen(false)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Subject</label>
                <input className={inputClass} value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} data-testid="campaign-subject-input" />
              </div>
              <div>
                <label className={labelClass}>Preview text</label>
                <input className={inputClass} value={campaignForm.preview_text} onChange={(e) => setCampaignForm({ ...campaignForm, preview_text: e.target.value })} data-testid="campaign-preview-input" />
              </div>
              <div>
                <label className={labelClass}>Body (HTML)</label>
                <textarea className={`${inputClass} font-mono text-xs resize-none`} rows={10} value={campaignForm.body_html} onChange={(e) => setCampaignForm({ ...campaignForm, body_html: e.target.value })} data-testid="campaign-body-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Audience</label>
                  <select className={inputClass} value={campaignForm.audience} onChange={(e) => setCampaignForm({ ...campaignForm, audience: e.target.value })} data-testid="campaign-audience-select">
                    <option value="all_contacts">All contacts</option>
                    <option value="custom">Custom list</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Scheduled for (optional)</label>
                  <input type="datetime-local" className={inputClass} value={campaignForm.scheduled_at} onChange={(e) => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })} data-testid="campaign-schedule-input" />
                </div>
              </div>
              {campaignForm.audience === "custom" && (
                <div>
                  <label className={labelClass}>Recipient emails (comma or newline separated)</label>
                  <textarea className={`${inputClass} resize-none`} rows={3} value={campaignForm.recipient_emails} onChange={(e) => setCampaignForm({ ...campaignForm, recipient_emails: e.target.value })} data-testid="campaign-recipients-input" />
                </div>
              )}
              <p className="text-[11px] text-gray-400">Saved as a draft first — send it from the campaign list once it's ready. Delivered from {MARKETING_EMAIL}.</p>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveCampaign} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="confirm-save-campaign">Save draft</Button>
              <Button onClick={() => setCampaignOpen(false)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Social post modal ── */}
      {socialOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">{editingSocialId ? "Edit social post" : "New social post"}</h2>
              <button onClick={() => setSocialOpen(false)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Platform</label>
                  <select className={inputClass} value={socialForm.platform} onChange={(e) => setSocialForm({ ...socialForm, platform: e.target.value })} data-testid="social-platform-select">
                    {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select className={inputClass} value={socialForm.status} onChange={(e) => setSocialForm({ ...socialForm, status: e.target.value })} data-testid="social-status-select">
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="posted">Posted</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Content</label>
                <textarea className={`${inputClass} resize-none`} rows={4} value={socialForm.content} onChange={(e) => setSocialForm({ ...socialForm, content: e.target.value })} data-testid="social-content-input" />
              </div>
              <div>
                <label className={labelClass}>Media URL</label>
                <input className={inputClass} value={socialForm.media_url} onChange={(e) => setSocialForm({ ...socialForm, media_url: e.target.value })} data-testid="social-media-input" />
              </div>
              <div>
                <label className={labelClass}>Link URL</label>
                <input className={inputClass} value={socialForm.link_url} onChange={(e) => setSocialForm({ ...socialForm, link_url: e.target.value })} data-testid="social-link-input" />
              </div>
              <div>
                <label className={labelClass}>Scheduled for</label>
                <input type="datetime-local" className={inputClass} value={socialForm.scheduled_at} onChange={(e) => setSocialForm({ ...socialForm, scheduled_at: e.target.value })} data-testid="social-schedule-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveSocialPost} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="confirm-save-social">
                {editingSocialId ? "Save changes" : "Add to calendar"}
              </Button>
              <Button onClick={() => setSocialOpen(false)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Marketing request modal ── */}
      {requestOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-900 text-lg">New marketing request</h2>
              <button onClick={() => setRequestOpen(false)} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Title</label>
                <input className={inputClass} value={requestForm.title} onChange={(e) => setRequestForm({ ...requestForm, title: e.target.value })} data-testid="request-title-input" />
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea className={`${inputClass} resize-none`} rows={4} value={requestForm.description} onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })} data-testid="request-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Type</label>
                  <select className={inputClass} value={requestForm.request_type} onChange={(e) => setRequestForm({ ...requestForm, request_type: e.target.value })} data-testid="request-type-select">
                    {REQUEST_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Priority</label>
                  <select className={inputClass} value={requestForm.priority} onChange={(e) => setRequestForm({ ...requestForm, priority: e.target.value })} data-testid="request-priority-select">
                    {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Due date</label>
                <input type="date" className={inputClass} value={requestForm.due_date} onChange={(e) => setRequestForm({ ...requestForm, due_date: e.target.value })} data-testid="request-due-input" />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveRequest} className="flex-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="confirm-save-request">Submit request</Button>
              <Button onClick={() => setRequestOpen(false)} variant="outline" className="rounded-full border-gray-200">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
